/**
 * NewGamePage for manually starting a new game.
 *
 * User enters:
 * 1. Paper ID (with checksum validation)
 * 2. Team 1 (autocomplete from teams in block)
 * 3. Team 2 (autocomplete from teams in block)
 */

import { useState, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Autocomplete,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { judgeApi } from '@/api/client';
import { useGameState } from '@/stores/gameStore';
import { useAuthStore } from '@/stores/authStore';
import { usePaperId } from '@/features/judge/hooks/usePaperId';
import type { JudgeTeam } from '@/types';

export function NewGamePage() {
  const navigate = useNavigate();
  const { startGame } = useGameState();
  const { user } = useAuthStore();
  const paperIdInput = usePaperId();

  const [team1, setTeam1] = useState<JudgeTeam | null>(null);
  const [team2, setTeam2] = useState<JudgeTeam | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Track previous paperId to reset selections when it changes
  // This pattern is recommended by React docs for adjusting state based on props
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const prevPaperIdRef = useRef<number | null>(null);
  // eslint-disable-next-line react-hooks/refs
  if (paperIdInput.paperId !== prevPaperIdRef.current) {
    // eslint-disable-next-line react-hooks/refs
    prevPaperIdRef.current = paperIdInput.paperId;
    // Reset state during render (safe because we're setting to initial values)
    if (team1 !== null) setTeam1(null);
    if (team2 !== null) setTeam2(null);
    if (error !== null) setError(null);
  }

  // Fetch teams and block info when paper ID is valid
  const {
    data: blockData,
    isLoading: isLoadingTeams,
    error: teamsError,
  } = useQuery({
    queryKey: ['judgeTeams', paperIdInput.paperId],
    queryFn: () => judgeApi.getTeams(paperIdInput.paperId!),
    enabled: !!paperIdInput.paperId,
  });

  const teams = blockData?.teams ?? [];
  const round = blockData?.round ?? 1;
  const league = blockData?.league_id ?? 'yle';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate paper ID
    if (!paperIdInput.isValid || !paperIdInput.paperId) {
      setError('Lohkon ID ei ole oikein, numero ja tarkistussumma eivät täsmää');
      return;
    }

    // Validate teams
    if (!team1 || !team2) {
      setError('Valitse molemmat joukkueet');
      return;
    }

    if (team1.id === team2.id) {
      setError('Joukkueet eivät voi olla sama');
      return;
    }

    // Start the game
    startGame(paperIdInput.value, paperIdInput.paperId, round, league, team1, team2, user?.id);
    navigate(`/judge/game/${paperIdInput.value}`);
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Uusi peli
      </Typography>

      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {teamsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Joukkueita ei voitu hakea Tulospalvelusta, mutta voit silti jatkaa
            pelin ilmoittamista
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Paper ID Input */}
          <TextField
            label="Lohkon ID"
            value={paperIdInput.value}
            onChange={(e) => paperIdInput.onChange(e.target.value)}
            fullWidth
            required
            autoComplete="off"
            error={!!paperIdInput.error}
            helperText={
              paperIdInput.error ||
              'Lohkon ID löytyy tuloslapusta (4 numeroa + kirjain)'
            }
            sx={{ mb: 3 }}
            inputProps={{
              style: { fontSize: '1.5rem', textAlign: 'center' },
            }}
          />

          {/* Team 1 Autocomplete */}
          <Autocomplete
            value={team1}
            onChange={(_, newValue) => setTeam1(newValue)}
            options={teams}
            getOptionLabel={(option) => option.name}
            loading={isLoadingTeams}
            disabled={!paperIdInput.isValid}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Joukkue 1"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingTeams && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          {/* Team 2 Autocomplete */}
          <Autocomplete
            value={team2}
            onChange={(_, newValue) => setTeam2(newValue)}
            options={teams.filter((t) => t.id !== team1?.id)}
            getOptionLabel={(option) => option.name}
            loading={isLoadingTeams}
            disabled={!paperIdInput.isValid}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Joukkue 2"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingTeams && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              component={RouterLink}
              to="/judge"
            >
              Peruuta
            </Button>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!paperIdInput.isValid || !team1 || !team2}
            >
              Aloita peli
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}


